/**
 * Minimap — top-right zone map. Shows the whole current zone: asteroids as green
 * dots (sized by ore), moons purple, the planet a big blue dot, the player a
 * yellow dot, and enemies red. Zoomable with the +/− buttons. Drawn on a canvas
 * from the snapshot's `minimap` data (refreshed a few times per second).
 */
import { useEffect, useRef, useState, type MouseEvent } from "react";
import type { MinimapData } from "@/engine/GameState";

const SIZE = 158; // canvas CSS px

export default function Minimap({
  data,
  onTarget,
  onWaypoint,
}: {
  data: MinimapData;
  onTarget?: (worldX: number, worldY: number) => void;
  onWaypoint?: (worldX: number, worldY: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);

  // Convert a tap on the minimap back to world coordinates (mirrors the draw transform below).
  // Used by the Mk VIII rift: while armed, tapping the map sets the teleport exit point.
  const handleTap = (e: MouseEvent<HTMLCanvasElement>) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const half = SIZE / 2;
    const s = (half / Math.max(1, data.extent)) * zoom;
    const halfWorld = half / s;
    const lim = Math.max(0, data.extent - halfWorld);
    const cx = Math.max(-lim, Math.min(lim, data.player.x));
    const cy = Math.max(-lim, Math.min(lim, data.player.y));
    const canvasX = ((e.clientX - rect.left) * SIZE) / rect.width;
    const canvasY = ((e.clientY - rect.top) * SIZE) / rect.height;
    const wx = cx + (canvasX - half) / s;
    const wy = cy + (canvasY - half) / s;
    if (data.riftArmed) onTarget?.(wx, wy); // Mk VIII: set the rift teleport exit
    else onWaypoint?.(wx, wy); // otherwise: click-to-move — fly the ship to this point
  };

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (cv.width !== SIZE * dpr) {
      cv.width = SIZE * dpr;
      cv.height = SIZE * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.fillStyle = "rgba(6,10,18,0.82)";
    ctx.fillRect(0, 0, SIZE, SIZE);

    const half = SIZE / 2;
    const s = (half / Math.max(1, data.extent)) * zoom;
    // Zoom in toward the PLAYER (yellow dot): follow the player, clamped so we
    // never pan past the edge of the zone. At zoom 1 the clamp pins the view to
    // the origin, so the whole map still fits.
    const halfWorld = half / s;
    const lim = Math.max(0, data.extent - halfWorld);
    const cx = Math.max(-lim, Math.min(lim, data.player.x));
    const cy = Math.max(-lim, Math.min(lim, data.player.y));
    const toX = (x: number) => half + (x - cx) * s;
    const toY = (y: number) => half + (y - cy) * s;
    const dot = (x: number, y: number, r: number, color: string) => {
      ctx.beginPath();
      ctx.arc(toX(x), toY(y), r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    };

    if (data.isSystem) {
      dot(0, 0, 4.5, "#ffca6a"); // star
      for (const p of data.planets) {
        const special = p.service !== "none";
        if (data.highlightSafe && special) {
          // Tutorial: ring the safe-zone planets so the player heads there.
          ctx.beginPath();
          ctx.arc(toX(p.x), toY(p.y), 9, 0, Math.PI * 2);
          ctx.strokeStyle = "#ffd24a";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
        if (p.rogue || special) {
          // Rogue worlds show a pirate flag; service worlds show their icon.
          const icon = p.rogue
            ? "🏴‍☠️"
            : p.service === "mechanical"
              ? "🔧"
              : p.service === "tavern"
                ? "🍺"
                : "🛒";
          ctx.font = "12px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji', sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(icon, toX(p.x), toY(p.y));
        } else {
          dot(p.x, p.y, 3.6, "#4f9bff");
        }
      }
    } else {
      if (data.planet) dot(data.planet.x, data.planet.y, 6.5, "#3d7bff");
      for (const m of data.moons) dot(m.x, m.y, 2.4, "#b98cff");
      for (const a of data.asteroids) {
        dot(a.x, a.y, a.s === 2 ? 2.7 : a.s === 1 ? 1.8 : 1.1, "#5fe08a");
      }
      // Cruisers (s: 2) show as a big bright-red dot; fighters a small red dot.
      for (const e of data.enemies)
        dot(e.x, e.y, e.s === 2 ? 5.4 : 2.8, e.s === 2 ? "#ff2020" : "#ff4a4a");
      // Our escort fighters — small blue dots.
      for (const f of data.playerFighters) dot(f.x, f.y, 2.2, "#4aa8ff");
    }
    // Click-to-move waypoint: a dashed line from the player to the destination + a ring marker.
    if (data.waypoint) {
      const wx = toX(data.waypoint.x);
      const wy = toY(data.waypoint.y);
      ctx.strokeStyle = "rgba(95,208,255,0.75)";
      ctx.lineWidth = 1.2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(toX(data.player.x), toY(data.player.y));
      ctx.lineTo(wx, wy);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(wx, wy, 5, 0, Math.PI * 2);
      ctx.strokeStyle = "#7fdcff";
      ctx.lineWidth = 1.6;
      ctx.stroke();
      dot(data.waypoint.x, data.waypoint.y, 1.5, "#eafcff");
    }
    dot(data.player.x, data.player.y, 3.1, "#ffe14a"); // player last, on top

    ctx.strokeStyle = "rgba(120,150,190,0.5)";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, SIZE - 1, SIZE - 1);
  }, [data, zoom]);

  return (
    <div className="hud-minimap">
      <canvas
        ref={canvasRef}
        className="minimap-canvas"
        onClick={handleTap}
        style={{
          width: SIZE,
          height: SIZE,
          cursor: data.riftArmed ? "crosshair" : "pointer",
          outline: data.riftArmed ? "2px solid #b98cff" : "none",
          outlineOffset: "-2px",
        }}
      />
      <div className="minimap-zoom">
        <button onClick={() => setZoom((z) => Math.min(6, z * 1.4))} aria-label="Zoom in">
          +
        </button>
        <button onClick={() => setZoom((z) => Math.max(1, z / 1.4))} aria-label="Zoom out">
          −
        </button>
      </div>
    </div>
  );
}
